import { Injectable, inject } from '@angular/core';
import { State, Selector, Action, StateContext } from '@ngxs/store';
import { append, iif, patch, removeItem } from '@ngxs/store/operators';
import { Observable, tap } from 'rxjs';
import { LoadingModel } from 'src/app/_core/state/loading.model';
import { DeleteAllFavorite, DeleteFavorite, GetPaginatedFavorites, PostFavorite } from './favorites.actions';
import { Favorite, PaginatedFavorites } from './models/favorites.response';
import { FavoritesService } from './services/favorites.service';
import { ToastService } from '../../../_core/services/toast.service';

export type FavoritesStateModel = LoadingModel & PaginatedFavorites & { favoriteAdded: Favorite; favoriteRemoved: Favorite };

export const initialState: FavoritesStateModel = {
  items: [],
  itemsPerPage: 0,
  itemsTotal: 0,
  numPages: 0,
  page: 0,
  favoriteAdded: undefined,
  favoriteRemoved: undefined,
  isLoading: false,
};

@State<FavoritesStateModel>({
  name: 'Favorites',
  defaults: initialState,
})
@Injectable()
export class FavoritesState {
  private readonly favoritesService = inject(FavoritesService);
  private readonly toastService = inject(ToastService);

  @Selector()
  static getPaginatedFavorites(state: FavoritesStateModel) {
    return {
      items: state.items,
      itemsPerPage: state.itemsPerPage,
      itemsTotal: state.itemsTotal,
      numPages: state.numPages,
      page: state.page
    };
  }

  @Selector()
  static isLoading(state: FavoritesStateModel) {
    return state.isLoading;
  }

  @Selector()
  static favoriteAdded(state: FavoritesStateModel) {
    return state.favoriteAdded;
  }

  @Selector()
  static favoriteRemoved(state: FavoritesStateModel) {
    return state.favoriteRemoved;
  }

  @Action(GetPaginatedFavorites)
  getPaginatedFavorites(context: StateContext<FavoritesStateModel>, { limit, offset }: GetPaginatedFavorites): Observable<PaginatedFavorites> {
    context.setState(patch<FavoritesStateModel>({ isLoading: true }));

    return this.favoritesService.getPaginatedFavorites(limit, offset).pipe(
      tap((paginatedFavorites) => {
        const { page } = context.getState();
        context.setState(
          patch<FavoritesStateModel>({
            items: iif(page >= paginatedFavorites.page, paginatedFavorites.items, append<Favorite>(paginatedFavorites.items)),
            itemsTotal: paginatedFavorites.itemsTotal,
            page: paginatedFavorites.page,
            numPages: paginatedFavorites.numPages,
            isLoading: false,
          })
        );
      })
    );
  }

  @Action(PostFavorite)
  postFavorite(context: StateContext<FavoritesStateModel>, { favoriteId }: PostFavorite) {
    return this.favoritesService.postFavorite(favoriteId).pipe(
      tap((newFavorite: Favorite) => {
        const state = context.getState();

        context.setState(
          patch<FavoritesStateModel>({
            items: append<Favorite>([newFavorite]),
            itemsTotal: state.itemsTotal + 1,
            favoriteAdded: newFavorite,
          }),
        );

        this.toastService.showToast('messages.favorites.add');

        context.setState(
          patch<FavoritesStateModel>({
            favoriteAdded: undefined,
          })
        );
      })
    );
  }

  @Action(DeleteFavorite)
  deleteFavorite(context: StateContext<FavoritesStateModel>, { favoriteId }: DeleteFavorite) {
    return this.favoritesService.deleteFavorite(favoriteId).pipe(
      tap((fav: Favorite[]) => {
        const state = context.getState();
        const newItemsTotal = Math.max(state.itemsTotal - 1, 0);

        context.setState(
          patch<FavoritesStateModel>({
            items: removeItem<Favorite>((favorite) => favorite.recordId === favoriteId),
            itemsTotal: newItemsTotal,
            favoriteRemoved: fav[0],
          })
        );

        this.toastService.showToast('messages.favorites.remove');
      })
    );
  }

  @Action(DeleteAllFavorite)
  deleteAllFavorites(context: StateContext<FavoritesStateModel>) {
    return this.favoritesService.deleteAllFavorites().pipe(
      tap(() => {
        context.setState(initialState);

        this.toastService.showToast('messages.favorites.clear');
      })
    );
  }
}
